import { CSSProperties, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { AgentEvent } from '~/atoms/schemas/workflowSchemas';
import { type TaskEntry } from '~/lib/workflows/useWorkflows';
import { ProcessedEvent } from './types';
import { useConversationState } from './utils/useConversationState';

// Styles as a React object instead of a string
const styles: Record<string, CSSProperties> = {
  conversationThread: {
    position: 'relative',
  },
  conversationEvents: {
    position: 'relative',
  },
  eventContainer: {
    position: 'relative',
  },
  eventContent: {
    padding: '0.75rem',
    transition: 'all 0.3s ease',
    marginLeft: '0.625rem', // Align with the center of the icon
    position: 'relative',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  toolCallData: {
    borderRadius: '0.25rem',
    padding: '0.5rem',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    marginTop: '0.5rem',
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '0.25rem',
    paddingLeft: '0.1rem',
  },
  eventIcon: {
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '0.2rem',
    color: 'white',
  },
};

// Helper to recursively process S3 URLs in an object
const processS3Urls = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(processS3Urls);
  }
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        k,
        processS3Urls(v),
      ]),
    );
  }
  if (typeof obj === 'string' && obj.startsWith('s3://')) {
    const parts = obj.split('/');
    return parts[parts.length - 1];
  }
  return obj;
};

// Helper component to format tool call data
const ToolCallData = ({ data }: { data: string }) => {
  try {
    const parsed = JSON.parse(data);
    if (!parsed) return <div>{data}</div>;

    // Format into readable JSON
    return (
      <div className="p-2">
        {Object.entries(parsed).map(([key, value]) => (
          <div key={key} className="mb-2 flex flex-column">
            <span className="text-sm text-color-secondary font-medium mb-1">
              {key}:{' '}
            </span>
            {typeof value === 'object' ? (
              <div
                className="border-round p-2 overflow-auto"
                style={{ maxHeight: '200px' }}
              >
                <pre
                  className="text-xs m-0 font-code"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {JSON.stringify(processS3Urls(value), null, 2)}
                </pre>
              </div>
            ) : (
              <span
                className="text-sm font-normal"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {String(value)}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  } catch (e) {
    return <div className="p-2 text-sm text-color-secondary">{data}</div>;
  }
};

// Component for the auto-scroll toggle button
const TailModeToggle = ({
  tailMode,
  setTailMode,
}: {
  tailMode: boolean;
  setTailMode: (value: boolean) => void;
}) => (
  <div style={{ position: 'absolute', right: '8px', top: '8px', zIndex: 10 }}>
    <button
      className={`p-button p-button-outlined ${tailMode ? 'p-button-success' : 'p-button-secondary'}`}
      onClick={() => setTailMode(!tailMode)}
      title={tailMode ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
      style={{
        padding: '0.15rem 0.3rem',
        fontSize: '0.75rem',
        minWidth: 'auto',
        lineHeight: 1,
      }}
    >
      <i
        className={`pi ${tailMode ? 'pi-sort-amount-down' : 'pi-sort-amount-down-alt'}`}
        style={{ fontSize: '0.6rem', marginRight: '2px' }}
      ></i>
      Tail
    </button>
  </div>
);

// Helper to replace S3 URLs in a string with just the filename
const replaceS3UrlsInString = (input: string): string =>
  input.replace(/s3:\/\/[^\s/]+(?:\/[^\s/]+)*\/([^\s/]+)/g, '$1');

// Helper component for rendering a single event
const EventItem = ({
  event,
}: {
  event: ProcessedEvent;
  eventIndex: number;
}) => {
  const isThinking = event.eventName.startsWith('thinking');
  const isResponse = event.eventName.startsWith('response');
  const isText = event.eventName.startsWith('text');
  const isToolCall = event.eventName.startsWith('tool_call');

  // Get icon background color based on event type
  const getIconBackgroundColor = () => {
    if (isThinking) return '#6366F1';
    if (isText) return '#22C55E';
    if (isResponse) return '#000000';
    return '#F97316'; // tool call
  };

  // Get event type label
  const getEventTypeLabel = () => {
    if (isThinking) return 'Analyzing';
    if (isResponse) return 'Response';
    if (isText) return 'Agent';
    return 'Tool Call';
  };

  // Helper to get an icon for event type
  const getEventIcon = () => {
    if (isThinking) {
      return <i className="pi pi-brain text-sm"></i>;
    } else if (isText) {
      return <i className="pi pi-comment text-sm"></i>;
    } else if (isToolCall) {
      return <i className="pi pi-wrench text-sm"></i>;
    }
    return <i className="pi pi-info-circle text-sm"></i>;
  };

  // Get content style based on event type
  const getContentStyle = () => {
    return { ...styles.eventContent };
  };

  const iconBackgroundColor = getIconBackgroundColor();
  const eventTypeLabel = getEventTypeLabel();
  const contentStyle = getContentStyle();

  return (
    <div
      style={styles.eventContainer}
      className="mb-3 fade-in animation-duration-300"
      data-event-type={event.eventName}
    >
      <div
        style={styles.eventHeader}
        className="flex align-items-center mb-2 relative"
      >
        <div
          style={{
            ...styles.eventIcon,
            backgroundColor: iconBackgroundColor,
            position: 'relative',
          }}
          className="flex-shrink-0"
        >
          {getEventIcon()}
        </div>
        <div className="text-sm text-color-secondary pl-1 font-medium">
          {eventTypeLabel}
        </div>
      </div>

      <div
        style={{
          ...contentStyle,
          borderLeft: `2px solid ${isThinking ? '#6366F1' : isText ? '#22C55E' : isResponse ? '#FFFFFF' : '#F97316'}`,
          marginTop: '-2px',
        }}
        className="shadow-1"
      >
        {isToolCall ? (
          <ToolCallData data={event.content} />
        ) : (
          <div className="text-sm">
            <Markdown
              remarkPlugins={[remarkBreaks]}
              components={{
                ul: (props) => (
                  <ul style={{ paddingLeft: '1rem' }} {...props} />
                ),
              }}
            >
              {replaceS3UrlsInString(event.content)}
            </Markdown>
          </div>
        )}
      </div>
    </div>
  );
};

export const AgentConversation = ({
  events,
  tasks,
}: {
  events: AgentEvent[];
  tasks: TaskEntry[];
}) => {
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const [tailMode, setTailMode] = useState<boolean>(false);
  const { processedEvents } = useConversationState(events, tasks);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    // Only auto-scroll if tail mode is enabled
    if (!tailMode) return;

    // Add a slight delay to ensure DOM has updated before scrolling
    const scrollTimeout = setTimeout(() => {
      if (conversationEndRef.current) {
        conversationEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, [processedEvents, events.length, tailMode]);

  return (
    <div className="p-2 surface-card border-round relative">
      <TailModeToggle tailMode={tailMode} setTailMode={setTailMode} />

      {processedEvents.length === 0 && (
        <div className="flex align-items-center justify-content-center h-full">
          <i className="pi pi-spin pi-spinner mr-2"></i>
          <span className="text-color-secondary">Processing workflow...</span>
        </div>
      )}

      <div style={styles.conversationThread} className="h-full">
        <div style={styles.conversationEvents} className="pl-0 h-full">
          {processedEvents.map((event, eventIndex) => (
            <EventItem
              key={`${event.uuid_run}-${event.index}-${eventIndex}`}
              event={event}
              eventIndex={eventIndex}
            />
          ))}
        </div>
      </div>

      {/* Invisible element at the end to scroll to */}
      <div
        ref={conversationEndRef}
        style={{ float: 'left', clear: 'both', height: '1px', width: '100%' }}
      />
    </div>
  );
};
